import { Injectable } from "@angular/core";
import { Observable, map } from "rxjs";
import { StaffAgreementsService } from "lib-klab-sdks";
import { EmploymentAgreementsPort } from "../../../app/ports/employment/employment-agreements.port";
import {
  AgreementStatus,
  AgreementSignature,
  AgreementSignatureRequest,
  AgreementTemplate,
  EmploymentAgreement,
  EmploymentAgreementCreateRequest,
} from "../../../domain/models/employment/agreement.model";
import {
  mapAgreementTemplate,
  mapAgreementSignature,
  mapEmploymentAgreement,
} from "../../mappers/employment/agreement.mapper";

@Injectable({ providedIn: "root" })
export class EmploymentAgreementsPortAdapter implements EmploymentAgreementsPort {
  constructor(private readonly api: StaffAgreementsService) {}

  list(status?: AgreementStatus): Observable<EmploymentAgreement[]> {
    return this.api
      .list({ status })
      .pipe(map((items) => items.map(mapEmploymentAgreement)));
  }

  listByContract(
    contractId: number,
    status?: AgreementStatus,
  ): Observable<EmploymentAgreement[]> {
    return this.api
      .list({
        sourceType: "HR_CONTRACT",
        sourceReferenceId: contractId,
        status,
      })
      .pipe(map((items) => items.map(mapEmploymentAgreement)));
  }

  getById(agreementId: number): Observable<EmploymentAgreement> {
    return this.api.getById(agreementId).pipe(map(mapEmploymentAgreement));
  }

  listActiveEmploymentTemplates(): Observable<AgreementTemplate[]> {
    return this.api
      .listTemplates("EMPLOYMENT", "ACTIVE")
      .pipe(map((items) => items.map(mapAgreementTemplate)));
  }

  createForContract(
    request: EmploymentAgreementCreateRequest,
  ): Observable<EmploymentAgreement> {
    return this.api
      .create({
        type: "EMPLOYMENT",
        title: request.title,
        templateId: request.templateId,
        sourceType: "HR_CONTRACT",
        sourceReferenceId: request.contractId,
        startDate: request.startDate,
        endDate: request.endDate,
        variables: request.variables,
        parties: [
          {
            role: "EMPLOYER",
            type: "COMPANY",
            referenceId: request.employer.companyId,
            displayName: request.employer.name,
            email: request.employer.email,
            signingOrder: 1,
            required: true,
          },
          {
            role: "EMPLOYEE",
            type: "EMPLOYEE",
            referenceId: request.employee.employeeId,
            displayName: request.employee.name,
            email: request.employee.email,
            signingOrder: 2,
            required: true,
          },
        ],
      })
      .pipe(map(mapEmploymentAgreement));
  }

  generate(
    agreementId: number,
    variables: Record<string, string>,
  ): Observable<EmploymentAgreement> {
    return this.api
      .generate(agreementId, { reason: "INITIAL", variables })
      .pipe(map(mapEmploymentAgreement));
  }

  submitForSignature(agreementId: number): Observable<EmploymentAgreement> {
    return this.api
      .submitForSignature(agreementId)
      .pipe(map(mapEmploymentAgreement));
  }

  listSignatures(agreementId: number): Observable<AgreementSignature[]> {
    return this.api
      .listSignatures(agreementId)
      .pipe(map((items) => items.map(mapAgreementSignature)));
  }

  sign(
    agreementId: number,
    partyId: number,
    request: AgreementSignatureRequest,
  ): Observable<AgreementSignature> {
    return this.api
      .sign(agreementId, partyId, request)
      .pipe(map(mapAgreementSignature));
  }

  terminate(
    agreementId: number,
    reason: string,
  ): Observable<EmploymentAgreement> {
    return this.api
      .terminate(agreementId, reason)
      .pipe(map(mapEmploymentAgreement));
  }

  cancel(agreementId: number): Observable<EmploymentAgreement> {
    return this.api.cancel(agreementId).pipe(map(mapEmploymentAgreement));
  }
}
